import { useState, memo } from "react";
import { Box, Rating, Typography, useMediaQuery } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import Slider from "../../common/ImgSlider";
import Tooltip from "../../../../components/Tooltip";
import UPCButton from "./CopyButton";
import ProductDetailBtnGroup from "./ProductDetailBtnGroup";

import { selectProductDetail } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { useAppSelector } from "../../../../store/hooks";
import { ZoomIn1 } from "../../../../utils";

import { useStyles } from "./index.styles";
import { COPY_ERROR } from "../../../../constants";

const ProductCard = () => {
  const { productDetail } = useAppSelector(selectProductDetail);

  const classes = useStyles();
  const [imageSlider, setImageSlider] = useState<boolean>(false);
  const isSmallScreen = useMediaQuery("(max-width:419px)");

  if (!productDetail) return null;

  const handleCopy = (type: string) => {
    const text = type === "ASIN" ? productDetail.asin : productDetail.upc;
    if (!text) return;
    navigator.clipboard.writeText(text).catch((error) => {
      console.error(error);
      enqueueSnackbar(COPY_ERROR, { variant: "error" });
    });
  };

  return (
    <Box className={classes.root} sx={isSmallScreen ? {alignItems: "flex-start"} : {}}>
      {imageSlider && productDetail.allImageUrl?.length && (
        <Slider
          images={productDetail.allImageUrl}
          setImageSlider={setImageSlider}
        />
      )}
      <Box
        className={classes.imgDiv}
        onClick={() => setImageSlider(true)}
        sx={
          isSmallScreen
            ? {
                width: "70px",
                height: "100px",
                padding: "2px",
                minWidth: "70px",
                position: "relative",
                borderRight: "none",
                "& img": {
                  maxWidth: "100%",
                  maxHeight: "100%",
                },
                "& .zoomInIcon": {
                  width: "14px",
                  height: "14px",
                },
              }
            : undefined
        }
      >
        <img
          src={ZoomIn1}
          alt="Zoom In"
          className={`${classes.zoomInIcon} zoomInIcon`}
        />

        <Box
          className={classes.productImgBtn}
          aria-label="Click to open image slider"
          sx={
            isSmallScreen
              ? {
                  "& img": {
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  },
                }
              : undefined
          }
        >
          <img src={productDetail.imageURL ?? ""} alt="Product" />
        </Box>
      </Box>
      <Box className={classes.infoDiv}>
        <Tooltip title={<Box bgcolor={"black"}>{productDetail.title}</Box>}>
          <Typography
            variant="h6"
            classes={{ root: classes.caption }}
            component="a"
            href={
              productDetail.asin
                ? `https://www.amazon.com/dp/${productDetail.asin}`
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              cursor: productDetail.asin ? "pointer" : "default",
              color: "inherit",
              textDecoration: "none",
              "&:hover": productDetail.asin
                ? {
                    color: "#0F56CA",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                  }
                : undefined,
            }}
          >
            {" "}
            {productDetail.title}
          </Typography>
        </Tooltip>

        <Typography variant="body2" className={classes.manufacturer}>
          by{" "}
          {productDetail.manufacturer && " " + productDetail.manufacturer + " "}
          | {productDetail.category}
        </Typography>
        <Box display={"flex"} alignItems={"center"} gap="2px">
          {productDetail.rating != null && productDetail.rating >= 0 && (
            <Rating
              precision={0.5}
              value={productDetail.rating}
              readOnly
              sx={{ fontSize: 12 }}
            />
          )}
          <Typography variant="caption" sx={{ mt: "2px" }}>
            {Number.isFinite(productDetail.review)
              ? productDetail.review?.toLocaleString("en-US")
              : "No reviews yet"}
          </Typography>
        </Box>
        <Box display={"flex"} flexDirection={"column"}>
          {productDetail.asin && (
            <UPCButton
              caption="ASIN"
              handleCopy={handleCopy}
              desc={productDetail.asin}
            />
          )}
          {productDetail.upc && (
            <UPCButton
              caption="UPC"
              handleCopy={handleCopy}
              desc={productDetail.upc}
            />
          )}
        </Box>
        <Box
          gap={0.5}
          display={"flex"}
          alignItems={"center"}
          mt={0.2}
          mb={0.2}
          sx={
            isSmallScreen
              ? {
                  gap: "2px",
                  flexWrap: "nowrap",
                  overflowX: "auto",
                }
              : undefined
          }
        >
          <ProductDetailBtnGroup productDetail={productDetail} />
        </Box>
      </Box>
    </Box>
  );
};

export default memo(ProductCard);
